/**
 * ApplicationDrawer — карточка заявки (правый drawer): что о ней
 * известно, WhatsApp, кто и когда рассмотрел, смена статуса.
 * Аккаунт клиента создаётся вручную в «Клиенты» после рассмотрения.
 *
 * Дровер живёт от `id`, а не от объекта заявки, и перечитывает её по
 * своей ручке. Очередь разбирают несколько человек: список,
 * загруженный десять минут назад, успевает устареть, и без этого
 * можно было «рассмотреть» заявку, которую коллега уже отклонил.
 * Строка из списка приходит отдельным пропом как первый кадр — чтобы
 * дровер открывался сразу, а не пустым. От `id` он зависит ещё и
 * потому, что умеет перепрыгнуть на заявку, которой в текущем
 * отфильтрованном списке нет вовсе (конфликт открытого лида).
 */

import {useEffect} from 'react';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {X} from 'lucide-react';

import {
  type ApplicationItem,
  applicationQueryOptions,
  type ApplicationStatus,
  applicationTitle,
  setApplicationStatus,
} from '@services/applications';
import {keys} from '@queries/keys';
import {Sheet, SheetContent} from '@components/ui/Sheet';

import {ApplicationActions} from '../ApplicationActions';
import {ApplicationFacts} from '../ApplicationFacts';
import {ApplicationNextStep} from '../ApplicationNextStep';
import {fmtAppDate} from '../Applications.utils';
import {AppStatusPill} from '../AppStatusPill';

type Props = {
  id: string | null;
  /** Строка из списка — первый кадр, пока летит свежая карточка */
  snapshot: ApplicationItem | null;
  onClose: () => void;
  /** Обновлённая заявка из ответа мутации — для снапшота и тоста */
  onStatusChanged: (updated: ApplicationItem) => void;
  /** Перейти к другой заявке очереди — к той, что мешает вернуть эту в работу */
  onOpenOther: (id: string) => void;
  /** Завести клиента по заявке — отдаём ту, что показана, а не снапшот списка */
  onConvert: (application: ApplicationItem) => void;
  /** Найти уже заведённого клиента — по телефону владельца */
  onOpenClient: (phone: string) => void;
};

export const ApplicationDrawer = ({
  id,
  snapshot,
  onClose,
  onStatusChanged,
  onOpenOther,
  onConvert,
  onOpenClient,
}: Props) => {
  const queryClient = useQueryClient();

  const fresh = useQuery(applicationQueryOptions(id));

  const mutation = useMutation({
    mutationFn: setApplicationStatus,
    onSuccess: (updated) => {
      void queryClient.invalidateQueries({queryKey: keys.applications.all});
      if (updated !== null) onStatusChanged(updated);
    },
  });

  /* Ошибка принадлежит заявке, а не дроверу: без сброса отказ по одной
     карточке висел бы в следующей, открытой из того же списка. */
  const {reset} = mutation;
  useEffect(() => reset(), [id, reset]);

  const handleSet = (status: ApplicationStatus) => {
    if (id !== null) mutation.mutate({id, status});
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) onClose();
  };

  /* Свежая карточка главнее строки списка, но пока она летит —
     показываем строку: пустой дровер читался бы как «не загрузилось». */
  const a = fresh.data ?? snapshot;

  return (
    <Sheet open={id !== null} onOpenChange={handleOpenChange}>
      <SheetContent
        side="right"
        showClose={false}
        className="flex w-[420px] max-w-[96vw] flex-col gap-0 p-0"
      >
        {a !== null && (
          <>
            <div className="border-b border-skr-line px-[22px] pb-4 pt-5">
              <div className="flex items-center justify-between">
                <AppStatusPill status={a.status} />
                <button
                  onClick={onClose}
                  aria-label="Закрыть"
                  className="grid size-8 place-items-center rounded-[9px] bg-secondary text-muted-foreground"
                >
                  <X className="size-[17px]" strokeWidth={2} />
                </button>
              </div>
              <h2 className="mt-3 text-lg font-bold -tracking-[.01em]">{applicationTitle(a)}</h2>
              <div className="mt-0.5 text-[12.5px] text-muted-foreground">
                {a.phoneVerified ? 'Заявка из приложения' : 'Заявка с сайта'} ·{' '}
                {fmtAppDate(a.createdAt)}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-[22px] py-[18px]">
              <ApplicationFacts application={a} />

              {a.comment !== null && (
                <div className="mt-3 rounded-[11px] border border-skr-line bg-card p-[13px] text-[13px] leading-[1.5]">
                  <div className="mb-1 text-[11.5px] font-semibold uppercase tracking-[.05em] text-skr-label">
                    Комментарий
                  </div>
                  {a.comment}
                </div>
              )}

              <ApplicationNextStep
                application={a}
                onConvert={onConvert}
                onOpenClient={onOpenClient}
              />
            </div>

            <ApplicationActions
              status={a.status}
              pending={mutation.isPending}
              error={mutation.error}
              onSet={handleSet}
              onOpenOther={onOpenOther}
            />
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
